import React, { useState, useEffect, useRef, useCallback } from 'react';

const DirectMessages = ({ 
  token, 
  currentUserId,
  onBack,
  onViewProfile,
  initialConversationUserId = null
}) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [isTyping, setIsTyping] = useState(null); // User ID of who is typing
  const [wsConnected, setWsConnected] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const ws = new WebSocket(`${WS_URL}/api/ws/dm/${token}`);
    
    ws.onopen = () => {
      console.log('DM WebSocket connected');
      setWsConnected(true);
      // Start ping interval to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
      ws.pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'new_message') {
        const msg = data.message;
        
        // Add message to current conversation if it's relevant
        if (selectedConversation && 
            (msg.sender_id === selectedConversation.user_id || 
             msg.recipient_id === selectedConversation.user_id)) {
          setMessages(prev => {
            // Check if message already exists
            if (prev.some(m => m.id === msg.id)) {
              return prev;
            }
            return [...prev, msg];
          });
          scrollToBottom();
          
          // Mark as read if we're viewing this conversation and it's from the other person
          if (msg.sender_id === selectedConversation.user_id) {
            ws.send(JSON.stringify({ 
              type: 'read', 
              sender_id: msg.sender_id 
            }));
          }
        }
        
        // Update conversations list
        setConversations(prev => {
          const otherUserId = msg.sender_id === currentUserId ? msg.recipient_id : msg.sender_id;
          const existingIdx = prev.findIndex(c => c.user_id === otherUserId);
          
          if (existingIdx >= 0) {
            const updated = [...prev];
            updated[existingIdx] = {
              ...updated[existingIdx],
              last_message: msg.content,
              last_message_time: msg.created_at
            };
            // Move to top
            const [conv] = updated.splice(existingIdx, 1);
            return [conv, ...updated];
          }
          return prev;
        });
        
        // Update unread count if not viewing this conversation
        if (msg.sender_id !== currentUserId && 
            (!selectedConversation || msg.sender_id !== selectedConversation.user_id)) {
          setUnreadCounts(prev => ({
            ...prev,
            [msg.sender_id]: (prev[msg.sender_id] || 0) + 1
          }));
        }
      }
      
      else if (data.type === 'typing') {
        // Show typing indicator
        setIsTyping(data.sender_id);
        // Clear after 3 seconds
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(null);
        }, 3000);
      }
      
      else if (data.type === 'messages_read') {
        // Update read status for our messages
        setMessages(prev => prev.map(m => 
          m.sender_id === currentUserId && m.recipient_id === data.reader_id
            ? { ...m, is_read: true }
            : m
        ));
      }
    };

    ws.onclose = () => {
      console.log('DM WebSocket disconnected');
      setWsConnected(false);
      if (ws.pingInterval) {
        clearInterval(ws.pingInterval);
      }
      // Attempt reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        connectWebSocket();
      }, 3000);
    };

    ws.onerror = (error) => {
      console.error('DM WebSocket error:', error);
    };

    wsRef.current = ws;
  }, [token, WS_URL, selectedConversation, currentUserId]);

  // Initialize WebSocket
  useEffect(() => {
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [connectWebSocket]);

  // Send typing indicator
  const sendTypingIndicator = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && selectedConversation) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        recipient_id: selectedConversation.user_id
      }));
    }
  }, [selectedConversation]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/private-conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
        
        // Calculate unread counts
        const counts = {};
        data.forEach(conv => {
          if (conv.unread_count > 0) {
            counts[conv.user_id] = conv.unread_count;
          }
        });
        setUnreadCounts(counts);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/private-messages/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        // Clear unread count for this conversation
        setUnreadCounts(prev => {
          const newCounts = { ...prev };
          delete newCounts[userId];
          return newCounts;
        });
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [API_URL, token]);

  // Search users for new conversation
  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await fetch(
        `${API_URL}/api/search?q=${encodeURIComponent(query)}&type=users&limit=10`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        // Filter out current user
        setSearchResults(data.users.filter(u => u.id !== currentUserId));
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  }, [API_URL, token, currentUserId]);

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || sendingMessage) return;
    
    setSendingMessage(true);
    try {
      const response = await fetch(`${API_URL}/api/private-messages`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_id: selectedConversation.user_id,
          content: newMessage.trim()
        })
      });
      
      if (response.ok) {
        const sentMessage = await response.json();
        setMessages(prev => [...prev, sentMessage]);
        setNewMessage('');
        scrollToBottom();
        
        // Update conversation in list
        setConversations(prev => {
          const existing = prev.find(c => c.user_id === selectedConversation.user_id);
          if (existing) {
            return prev.map(c => 
              c.user_id === selectedConversation.user_id
                ? { ...c, last_message: newMessage.trim(), last_message_time: new Date().toISOString() }
                : c
            ).sort((a, b) => new Date(b.last_message_time) - new Date(a.last_message_time));
          } else {
            return [{
              user_id: selectedConversation.user_id,
              nickname: selectedConversation.nickname,
              first_name: selectedConversation.first_name,
              last_name: selectedConversation.last_name,
              avatar_url: selectedConversation.avatar_url,
              last_message: newMessage.trim(),
              last_message_time: new Date().toISOString(),
              unread_count: 0
            }, ...prev];
          }
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  // Start new conversation with user
  const startConversation = (user) => {
    setSelectedConversation({
      user_id: user.id,
      nickname: user.nickname,
      first_name: user.first_name,
      last_name: user.last_name,
      avatar_url: user.avatar_url
    });
    setShowNewConversation(false);
    setSearchQuery('');
    setSearchResults([]);
    fetchMessages(user.id);
  };

  // Select conversation
  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    fetchMessages(conv.user_id);
    inputRef.current?.focus();
  };

  // Initial fetch
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Handle initial conversation
  useEffect(() => {
    if (initialConversationUserId && conversations.length > 0) {
      const conv = conversations.find(c => c.user_id === initialConversationUserId);
      if (conv) {
        selectConversation(conv);
      }
    }
  }, [initialConversationUserId, conversations]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Mark messages as read when selecting a conversation (via WebSocket)
  useEffect(() => {
    if (selectedConversation && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'read',
        sender_id: selectedConversation.user_id
      }));
    }
  }, [selectedConversation]);

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      searchUsers(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchUsers]);

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'acum';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}z`;
    return date.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' });
  };

  // Format message time (more detailed)
  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
  };

  // Get total unread count
  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="flex h-screen bg-gray-900" data-testid="direct-messages">
      {/* Conversations Sidebar */}
      <div className={`w-full md:w-80 lg:w-96 border-r border-gray-800 flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-2 hover:bg-gray-800 rounded-full transition-colors"
                  data-testid="dm-back-button"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <h1 className="text-xl font-bold text-white">Mesaje</h1>
              {totalUnread > 0 && (
                <span className="bg-cyan-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {totalUnread}
                </span>
              )}
            </div>
            <button
              onClick={() => setShowNewConversation(true)}
              className="p-2 hover:bg-gray-800 rounded-full transition-colors"
              data-testid="new-conversation-button"
              title="Mesaj nou"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          {/* Search in conversations */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Caută în conversații..."
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-full text-white placeholder-gray-500 text-sm focus:outline-none focus:border-cyan-500"
              data-testid="conversation-search"
            />
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-500"></div>
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-800 flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-gray-500">Nicio conversație încă</p>
              <button
                onClick={() => setShowNewConversation(true)}
                className="mt-4 px-4 py-2 bg-cyan-500 text-white rounded-full text-sm font-semibold hover:bg-cyan-600 transition-colors"
              >
                Începe o conversație
              </button>
            </div>
          ) : (
            conversations.map(conv => (
              <button
                key={conv.user_id}
                onClick={() => selectConversation(conv)}
                className={`w-full p-4 flex items-start space-x-3 hover:bg-gray-800/50 transition-colors border-b border-gray-800/50 ${
                  selectedConversation?.user_id === conv.user_id ? 'bg-gray-800/70' : ''
                }`}
                data-testid={`conversation-${conv.user_id}`}
              >
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 overflow-hidden">
                    {conv.avatar_url ? (
                      <img src={conv.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white font-bold">
                        {(conv.first_name?.[0] || conv.nickname?.[0] || 'U').toUpperCase()}
                      </div>
                    )}
                  </div>
                  {unreadCounts[conv.user_id] > 0 && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">
                        {unreadCounts[conv.user_id] > 9 ? '9+' : unreadCounts[conv.user_id]}
                      </span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center justify-between">
                    <span className={`font-semibold truncate ${unreadCounts[conv.user_id] > 0 ? 'text-white' : 'text-gray-300'}`}>
                      {conv.first_name} {conv.last_name}
                    </span>
                    <span className="text-gray-500 text-xs flex-shrink-0 ml-2">
                      {formatTime(conv.last_message_time)}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm">@{conv.nickname}</p>
                  {conv.last_message && (
                    <p className={`text-sm truncate mt-1 ${unreadCounts[conv.user_id] > 0 ? 'text-white font-medium' : 'text-gray-500'}`}>
                      {conv.last_message}
                    </p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col ${selectedConversation ? 'flex' : 'hidden md:flex'}`}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-800 flex items-center justify-between bg-gray-900/95 backdrop-blur-md">
              <div className="flex items-center space-x-3">
                {/* Mobile back button */}
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="md:hidden p-2 hover:bg-gray-800 rounded-full transition-colors"
                  data-testid="chat-back-button"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <button
                  onClick={() => onViewProfile && onViewProfile(selectedConversation.user_id)}
                  className="flex items-center space-x-3 hover:bg-gray-800 rounded-lg p-2 -ml-2 transition-colors"
                  data-testid="chat-user-profile"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 overflow-hidden">
                    {selectedConversation.avatar_url ? (
                      <img src={selectedConversation.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white font-bold">
                        {(selectedConversation.first_name?.[0] || selectedConversation.nickname?.[0] || 'U').toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-white">
                      {selectedConversation.first_name} {selectedConversation.last_name}
                    </p>
                    <p className="text-gray-500 text-sm">@{selectedConversation.nickname}</p>
                  </div>
                </button>
              </div>

              {/* Chat options */}
              <button className="p-2 hover:bg-gray-800 rounded-full transition-colors">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 overflow-hidden">
                    {selectedConversation.avatar_url ? (
                      <img src={selectedConversation.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white text-2xl font-bold">
                        {(selectedConversation.first_name?.[0] || selectedConversation.nickname?.[0] || 'U').toUpperCase()}
                      </div>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-white">
                    {selectedConversation.first_name} {selectedConversation.last_name}
                  </h3>
                  <p className="text-gray-500">@{selectedConversation.nickname}</p>
                  <p className="text-gray-600 mt-4">Începe o conversație nouă</p>
                </div>
              ) : (
                messages.map((msg, index) => {
                  const isOwn = msg.sender_id === currentUserId;
                  const showAvatar = !isOwn && (index === 0 || messages[index - 1].sender_id !== msg.sender_id);
                  
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${msg.id}`}
                    >
                      <div className={`flex items-end space-x-2 max-w-[75%] ${isOwn ? 'flex-row-reverse space-x-reverse' : ''}`}>
                        {/* Avatar for received messages */}
                        {!isOwn && (
                          <div className={`w-8 h-8 rounded-full flex-shrink-0 ${showAvatar ? '' : 'invisible'}`}>
                            {showAvatar && (
                              <div className="w-full h-full rounded-full bg-gradient-to-br from-purple-500 to-pink-500 overflow-hidden">
                                {msg.sender_avatar_url ? (
                                  <img src={msg.sender_avatar_url} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-white text-xs font-bold">
                                    {(msg.sender_nickname?.[0] || 'U').toUpperCase()}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Message bubble */}
                        <div className={`rounded-2xl px-4 py-2 ${
                          isOwn 
                            ? 'bg-cyan-500 text-white rounded-br-sm' 
                            : 'bg-gray-800 text-white rounded-bl-sm'
                        }`}>
                          <p className="break-words">{msg.content}</p>
                          <p className={`text-xs mt-1 ${isOwn ? 'text-cyan-100' : 'text-gray-500'}`}>
                            {formatMessageTime(msg.created_at)}
                            {isOwn && msg.is_read && (
                              <span className="ml-2">✓✓</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Typing Indicator */}
            {isTyping === selectedConversation?.user_id && (
              <div className="px-4 pb-2">
                <div className="flex items-center space-x-2 text-gray-400 text-sm">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <span>{selectedConversation.first_name} scrie...</span>
                </div>
              </div>
            )}

            {/* Message Input */}
            <div className="p-4 border-t border-gray-800">
              {/* WebSocket Status Indicator */}
              {!wsConnected && (
                <div className="mb-2 flex items-center justify-center space-x-2 text-yellow-500 text-xs">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                  <span>Se reconectează...</span>
                </div>
              )}
              
              <div className="flex items-center space-x-3">
                {/* Attachment button */}
                <button className="p-2 text-gray-400 hover:text-cyan-400 hover:bg-gray-800 rounded-full transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>

                {/* Text input */}
                <div className="flex-1 relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      sendTypingIndicator();
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Scrie un mesaj..."
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-full text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                    data-testid="message-input"
                    disabled={sendingMessage}
                  />
                </div>

                {/* Send button */}
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  className={`p-3 rounded-full transition-colors ${
                    newMessage.trim() && !sendingMessage
                      ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                      : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                  }`}
                  data-testid="send-message-button"
                >
                  {sendingMessage ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          /* No conversation selected */
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gray-800 flex items-center justify-center">
                <svg className="w-12 h-12 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Mesajele tale</h2>
              <p className="text-gray-500 mb-6">Selectează o conversație sau începe una nouă</p>
              <button
                onClick={() => setShowNewConversation(true)}
                className="px-6 py-3 bg-cyan-500 text-white rounded-full font-semibold hover:bg-cyan-600 transition-colors"
                data-testid="start-conversation-button"
              >
                Scrie un mesaj
              </button>
            </div>
          </div>
        )}
      </div>

      {/* New Conversation Modal */}
      {showNewConversation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center pt-20 p-4 z-50">
          <div className="bg-gray-900 rounded-2xl w-full max-w-lg border border-gray-800 shadow-xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => {
                    setShowNewConversation(false);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="p-2 hover:bg-gray-800 rounded-full transition-colors"
                  data-testid="close-new-conversation"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <h2 className="text-xl font-bold text-white">Mesaj nou</h2>
              </div>
            </div>

            {/* Search Input */}
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center space-x-2">
                <span className="text-gray-500">Către:</span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Caută o persoană..."
                  className="flex-1 bg-transparent text-white placeholder-gray-500 focus:outline-none"
                  data-testid="new-conversation-search"
                  autoFocus
                />
              </div>
            </div>

            {/* Search Results */}
            <div className="max-h-96 overflow-y-auto">
              {isSearching ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-cyan-500"></div>
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map(user => (
                  <button
                    key={user.id}
                    onClick={() => startConversation(user)}
                    className="w-full p-4 flex items-center space-x-3 hover:bg-gray-800 transition-colors"
                    data-testid={`new-conversation-user-${user.id}`}
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 overflow-hidden">
                      {user.avatar_url ? (
                        <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white font-bold">
                          {(user.first_name?.[0] || user.nickname?.[0] || 'U').toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-white">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="text-gray-500 text-sm">@{user.nickname}</p>
                    </div>
                  </button>
                ))
              ) : searchQuery.length >= 2 ? (
                <div className="p-8 text-center text-gray-500">
                  Niciun utilizator găsit
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  Caută un utilizator pentru a începe o conversație
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectMessages;
